import React from 'react';
import { Flame, Shield, Bell, CheckCircle, Building2, Radio, Cpu, FileCheck, Clock, AlertTriangle, Wifi, Zap, Eye, Monitor, Volume2, Settings } from 'lucide-react';
import QuoteForm from '../components/QuoteForm';

const SuperiorSSIPage = () => {
  // Products Type4 Line Fire Safety - Official Ajax Systems products with black product images
  const ssiProducts = [
    {
      name: "Type4 Fire Hub Jeweller",
      description: "Centrale incendie avec écran tactile",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/gybby5n9_image.png",
      specs: "Écran 10.1\" • Wi-Fi/4G/Ethernet • 200 appareils"
    },
    {
      name: "Type4 Hub (4G) Jeweller",
      description: "Centrale incendie compacte 4G",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/0zzjufmf_image.png",
      specs: "100 appareils • Ethernet/4G • 20h autonomie"
    },
    {
      name: "Type4 ManualCallPoint Jeweller",
      description: "Déclencheur manuel incendie rouge",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/r95fjdkg_image.png",
      specs: "Réinitialisable • 7 ans batterie • 1700m portée"
    },
    {
      name: "Type4 FireProtect (Sounder) Jeweller",
      description: "Sirène d'alarme incendie",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/4ybmngdh_image.png",
      specs: "100 dB • NF S32-001 • 5 ans batterie"
    },
    {
      name: "Type4 FireProtect (VAD) Jeweller",
      description: "Avertisseur visuel d'alarme",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/nqyi7yoc_image.png",
      specs: "LED périmétrique • Mur/Plafond • Type 4"
    },
    {
      name: "Type4 FireProtect (Sounder/VAD)",
      description: "Sirène + avertisseur visuel combiné",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/g8ohchxr_image.png",
      specs: "100 dB + LED • 2-en-1 • IP54"
    },
    {
      name: "Type4 I/O Module (2X2) Jeweller",
      description: "Module d'intégration 2 entrées/2 sorties",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/58ttfwds_image.png",
      specs: "NC/NO/EOL/3EOL • 5 ans batterie • IP21C"
    },
    {
      name: "Type4 Fire ReX Jeweller",
      description: "Prolongateur de portée incendie",
      image: "https://customer-assets.emergentagent.com/job_de3c48b1-8077-40dc-b04a-8b774c3b713a/artifacts/0tbpu1s7_image.png",
      specs: "1800m portée • Ethernet • 72h backup"
    },
  ];

  const certifications = [
    { code: 'Type 4', name: 'Norme française ERP', icon: Shield },
    { code: 'EN 54', name: 'Norme européenne incendie', icon: FileCheck },
    { code: 'NF S32-001', name: 'Signal sonore alarme', icon: CheckCircle },
    { code: 'IP54', name: 'Protection boîtier', icon: AlertTriangle },
  ];

  const keyAdvantages = [
    {
      title: "100% Sans fil Jeweller",
      description: "Installation sans câblage. Les appareils communiquent via le protocole radio Jeweller chiffré jusqu'à 1800m de portée.",
      icon: Wifi
    },
    {
      title: "Centrale avec écran tactile",
      description: "Type4 Fire Hub avec écran IPS 10.1\" pour contrôle intuitif des évacuations, alarmes et journal d'événements.",
      icon: Monitor
    },
    {
      title: "Alertes en 3 secondes",
      description: "Les sirènes et VAD s'activent en seulement 3 secondes après le déclenchement d'une alarme incendie.",
      icon: Zap
    },
    {
      title: "Sirène 100 dB + LED",
      description: "Avertisseurs sonores et visuels conformes NF S32-001 pour une évacuation efficace.",
      icon: Volume2
    },
  ];

  const detectionTech = [
    {
      title: "Protocole Jeweller",
      description: "Communication radio bidirectionnelle chiffrée avec détection de brouillage et saut de fréquence automatique.",
      features: ["Portée 1800m", "Chiffré", "Anti-brouillage"]
    },
    {
      title: "OS Malevich",
      description: "Système d'exploitation temps réel développé par Ajax. Immunisé contre les virus et cyberattaques.",
      features: ["Temps réel", "Mises à jour OTA", "Anti-virus"]
    },
    {
      title: "Supervision avancée",
      description: "Auto-diagnostic permanent de chaque appareil. Notifications instantanées en cas de défaut ou maintenance.",
      features: ["Auto-diagnostic", "Polling 36s", "Notifications push"]
    }
  ];

  const keyFeatures = [
    { icon: Radio, title: "Jeweller", desc: "Portée 1800m" },
    { icon: Clock, title: "Jusqu'à 7 ans", desc: "Autonomie batteries" },
    { icon: Zap, title: "3 secondes", desc: "Alerte instantanée" },
    { icon: Monitor, title: "Écran tactile", desc: "Contrôle central" },
    { icon: Shield, title: "Type 4", desc: "Certifié ERP France" },
    { icon: Volume2, title: "100 dB", desc: "Sirène puissante" },
  ];

  const targetBuildings = [
    { name: "Restaurants", desc: "Établissements de restauration", icon: Building2 },
    { name: "Commerces", desc: "Boutiques et magasins", icon: Building2 },
    { name: "Bureaux", desc: "Espaces de travail", icon: Building2 },
    { name: "Écoles", desc: "Établissements scolaires", icon: Building2 },
    { name: "Hôtels", desc: "Hébergements", icon: Building2 },
    { name: "ERP Type 4", desc: "Établissements publics", icon: Shield },
  ];

  return (
    <div className="min-h-screen py-12 bg-black" data-testid="superior-ssi-page">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden">
          <img 
            src="https://ajax.systems/api/cdn-img/?img=%2Fupload%2Ftype_4_line_xl_FR_36124dbccb%402.jpg" 
            alt="Type4 Line - Sécurité incendie Ajax"
            className="w-full h-[350px] md:h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent flex items-center">
            <div className="px-6 md:px-12 max-w-2xl">
              <p className="text-red-400 text-sm font-medium tracking-wide uppercase mb-3">Type4 Line • Certifié ERP</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
                Superior <span className="text-red-400">SSI EN54</span>
              </h1>
              <p className="text-base md:text-lg text-white/50 mb-6 font-light">
                Solution d'alarme incendie sans fil Type4 Line pour les établissements recevant du public.
              </p>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-fgs-primary/20 rounded-full">
                  <Shield className="w-4 h-4 text-emerald-400" />
                  <span className="text-white/70 text-sm">Garantie 4 ans</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                  <FileCheck className="w-4 h-4 text-white/50" />
                  <span className="text-white/70 text-sm">Sur devis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Grid */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {keyFeatures.map((feature, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 hover:border-red-500/20 rounded-xl p-4 text-center transition-colors">
              <feature.icon className="w-7 h-7 text-red-400 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-white mb-1">{feature.title}</h3>
              <p className="text-[10px] text-white/50">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Key Advantages */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight">
            Technologie fiable et unique
          </h2>
          <p className="text-white/60 max-w-3xl mx-auto font-light">
            Les détecteurs Ajax combinent des technologies brevetées, une connectivité avancée 
            et une conformité aux normes les plus strictes.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {keyAdvantages.map((adv, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 hover:border-red-500/20 rounded-2xl p-6 transition-colors">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <adv.icon className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">{adv.title}</h3>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{adv.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Detection Technology */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="bg-gradient-to-r from-red-900/30 to-fgs-surface border border-red-500/20 rounded-2xl p-8 md:p-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
            Technologies de détection avancées
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {detectionTech.map((tech, i) => (
              <div key={i} className="bg-black/40/50 rounded-xl p-6 border border-red-500/10">
                <h3 className="font-heading text-lg font-bold text-white mb-3">{tech.title}</h3>
                <p className="text-sm text-white/50 mb-4">{tech.description}</p>
                <ul className="space-y-2">
                  {tech.features.map((feat, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white">
                      <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
          Certifications
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {certifications.map((cert, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-5 text-center hover:border-red-500/40 transition-colors group">
              <cert.icon className="w-8 h-8 text-red-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <span className="text-red-400 font-mono font-bold block text-lg">{cert.code}</span>
              <p className="text-xs text-white/50 mt-2">{cert.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Target Buildings */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-4">
            Applications Type 4
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Solution sans fil adaptée aux établissements recevant du public en France.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {targetBuildings.map((building, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl p-4 text-center hover:border-red-500/40 transition-colors">
              <building.icon className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <h4 className="text-white font-semibold text-sm mb-1">{building.name}</h4>
              <p className="text-xs text-white/50">{building.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Gamme Type4 Line
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto">
            Système d'alarme incendie sans fil complet : centrales, sirènes, VAD, déclencheurs manuels et modules d'intégration.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {ssiProducts.map((product, i) => (
            <div 
              key={i} 
              className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:border-red-500/40 transition-all duration-300 group"
            >
              <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg p-4 mb-4 aspect-square flex items-center justify-center overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition-transform"
                />
              </div>
              <h3 className="font-heading text-sm font-bold text-white mb-1 line-clamp-2">{product.name}</h3>
              <p className="text-xs text-white/50 mb-2 line-clamp-1">{product.description}</p>
              <p className="text-xs text-red-400 font-medium">{product.specs}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase mb-8 text-center">
            Comment ça fonctionne
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '1', title: 'Déclenchement', desc: 'Appui sur le déclencheur manuel ou détection automatique' },
              { step: '2', title: 'Transmission', desc: 'Signal envoyé à la centrale via Jeweller en 3s' },
              { step: '3', title: 'Activation', desc: 'Sirènes 100 dB et VAD dans tout le bâtiment' },
              { step: '4', title: 'Notification', desc: 'Alerte app, station de télésurveillance' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-semibold text-red-400">{item.step}</span>
                </div>
                <h3 className="font-heading text-base font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-3xl font-semibold text-white uppercase mb-4">
              Demandez votre étude SSI
            </h2>
            <p className="text-white/50 mb-6">
              Nos experts en sécurité incendie dimensionnent votre installation Type4 Line
              selon les normes en vigueur et vos besoins spécifiques.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white">Audit de conformité Type 4 gratuit</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white">Plan d'implantation des équipements</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white">Installation par techniciens certifiés</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white">Garantie 4 ans FGS incluse</span>
              </div>
            </div>
          </div>
          <div>
            <QuoteForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuperiorSSIPage;
